import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb,
  faBolt,
  faCog,
  faChartBar,
  faArrowUp,
  faComments,
  faExchange,
  faNetworkWired,
} from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch } from "../../common/state/hooks";
import { useEffect } from "react";
import { update } from "../../common/state/Slices/HeaderMenuSlice";
import { Routes } from "../../routes/routes";
import styles from "./CaseStudyShimadzu.module.css";
import ShimadzuFirst from "../../images/case-studies/shimadzu/Shimadzu-1.png";
import ShimadzuSecond from "../../images/case-studies/shimadzu/Shimadzu-2.png";

const caseStudy = {
  title: "Shimadzu Multi-omics Analysis Package Powered by Garuda",
  overview:
    "Shimadzu Corporation collaborated with Garuda to develop the Multi-omics Analysis Package, a solution designed to streamline workflows and enhance data analysis in metabolic engineering. Using Garuda’s connectivity and integration capabilities, this package provides researchers with tools to analyze data across metabolomics, proteomics, and flux pathways effectively.",
  features: [
    {
      title: "Integration and Connectivity",
      description:
        "The Multi-omics Analysis Package leverages Garuda’s ability to connect diverse tools, enabling seamless workflows between Shimadzu’s analytical instruments and data processing components. This ensures interoperability across datasets and systems.",
    },
    {
      title: "Automated Multi-omics Metabolic Mapping",
      description:
        "The package automates the generation of metabolic maps together with enzyme or transporter abundance , allowing users to visualize changes in compound quantities efficiently and interpret the causality. Automation reduces manual effort and improves analysis consistency.",
    },
    {
      title: "Comprehensive Data Analysis Tools",
      description:
        "Equipped with features like volcano plots, PCA (principal component analysis), hierarchical clustering analysis (HCA), and box plots, the package supports in-depth exploration of complex datasets. These tools are linked to metabolic maps, simplifying the identification and interpretation of significant compounds.",
    },
    {
      title: "Preconfigured Templates for Faster Workflows",
      description:
        "The package includes templates compatible with Shimadzu’s method packages, such as those for primary metabolites, lipid mediators, and bile acids. These templates provide ready-to-use workflows, reducing setup time and ensuring compatibility with established methods.",
    },
    {
      title: "Modular Workflow Execution",
      description:
        "Through Garuda’s framework, Shimadzu’s tools operate as modular components that can be integrated into workflows, offering flexibility and scalability for different research needs.",
    },
  ],
  benefits: [
    {
      title: "Accelerated Analysis",
      description:
        "The package reduces the time needed to process and visualize large datasets, helping researchers focus on data interpretation.",
    },
    {
      title: "Streamlined Workflows",
      description:
        "Prebuilt templates and automation minimize setup and operational complexity.",
    },
    {
      title: "Scalable Solutions",
      description:
        "Modular design allows the integration of additional tools and workflows as research needs evolve.",
    },
    {
      title: "Multiomics interpretation",
      description:
        "Multiomics allows scientists to analyze and combine multiple types of molecular information within individual cells to gain a deeper and more comprehensive understanding of cellular behavior and function.",
    }
  ],
  role: {
    title: "Garuda’s Role",
    description:
      "Garuda serves as the backbone for connectivity and workflow integration in the Multi-omics Analysis Package. Its architecture supports seamless communication between tools, enabling researchers to work efficiently without being constrained by technical limitations.",
  },
  learn: {
    primaryText:
      "Visit Shimadzu’s Multi-omics Analysis Package: Shimadzu Multi-omics Analysis Package",
    secondaryText: "Explore the Garuda platform: Garuda Alliance Shimadzu Pack",
    links: [
      "https://www.shimadzu.com/an/products/liquid-chromatograph-mass-spectrometry/lc-ms-software/multi-omics-analysis-package/index.html",
      "http://www.garuda-alliance.org/gadgetpack/shimadzu/",
    ],
  },
};
const icons = [faLightbulb, faBolt, faCog, faChartBar, faArrowUp, faComments];

export const CaseStudyShimadzu = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      update({
        selectedMenu: Routes.CASE_STUDIES_SHIMADZU,
      })
    );
  }, [dispatch]);

  return (
    <Box>
      <Card
        key={caseStudy.title}
        sx={{
          margin: 2,
          borderRadius: 2,
          boxShadow:
            "0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)",
        }}
      >
        <CardContent>
          <Typography
            variant="h2"
            color="primary"
            gutterBottom
            sx={{ fontWeight: "bold", mt: 2 }}
          >
            <span style={{ color: "#454545" }}>Case Study: </span>
            {caseStudy.title}
          </Typography>
          <Typography variant="body2" paragraph sx={{ mt: 4 }}>
            {caseStudy.overview}
          </Typography>
          <div className={styles.caseStudyImageContainer}>
            <div className={styles.caseStudyImageWrapper}>
              <img
                src={ShimadzuFirst}
                alt="Shimadzu First"
                className={styles.image}
              />
            </div>
            <div className={styles.caseStudyImageWrapper}>
              <img
                src={ShimadzuSecond}
                alt="Shimadzu Second"
                className={styles.image}
              />
            </div>
          </div>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Key Features:
          </Typography>
          <List>
            {caseStudy.features.map((feature, idx) => (
              <ListItem key={idx} sx={{ alignItems: "baseline" }}>
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <FontAwesomeIcon icon={icons[idx % icons.length]} size="lg" />
                </ListItemIcon>
                <ListItemText
                  sx={{ textAlign: "start", margin: "0 1rem" }}
                  primary={
                    <>
                      <strong>{feature.title}:</strong> {feature.description}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Applications and Benefits:
          </Typography>
          <List>
            {caseStudy.benefits.map((benefit, idx) => (
              <ListItem key={idx} sx={{ alignItems: "baseline" }}>
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <FontAwesomeIcon icon={icons[idx % icons.length]} size="lg" />
                </ListItemIcon>
                <ListItemText
                  sx={{ textAlign: "start", margin: "0 1rem" }}
                  primary={
                    <>
                      <strong>{benefit.title}:</strong> {benefit.description}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Garuda's Role:
          </Typography>
          <List>
            <ListItem
              key={caseStudy.role.title}
              sx={{ alignItems: "baseline" }}
            >
              <ListItemIcon sx={{ minWidth: "auto" }}>
                <FontAwesomeIcon icon={faExchange} size="lg" />
              </ListItemIcon>
              <ListItemText
                sx={{ textAlign: "start", margin: "0 1rem" }}
                primary={
                  <>
                    <strong>{caseStudy.role.title}:</strong>{" "}
                    {caseStudy.role.description}
                  </>
                }
              />
            </ListItem>
          </List>
          <Divider sx={{ marginY: 2 }} />
          <Typography variant="h3" gutterBottom sx={{ fontWeight: "500" }}>
            Learn More:
          </Typography>

          <List>
            <ListItem
              key={caseStudy.learn.primaryText}
              sx={{ alignItems: "baseline" }}
            >
              <ListItemIcon sx={{ minWidth: "auto" }}>
                <FontAwesomeIcon icon={faNetworkWired} size="lg" />
              </ListItemIcon>
              <ListItemText
                sx={{
                  textAlign: "start",
                  margin: "0 1rem",
                  "& .MuiTypography-root": {
                    fontWeight: 400,
                  },
                  "& .MuiTypography-body2": {
                    color: "text.primary",
                  },
                }}
                primary={caseStudy.learn.primaryText}
                secondary={caseStudy.learn.secondaryText}
              />
            </ListItem>
            {caseStudy.learn.links.map((link, idx) => (
              <ListItem key={link} sx={{ alignItems: "baseline", padding: 0 }}>
                <ListItemText
                  sx={{
                    textAlign: "start",
                    margin: "8",
                  }}
                  primary={
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: "1rem", display: "block" }}
                    >
                      {link}
                    </a>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};
